import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as webSocketAPI from "../../actions/zoomboard/liveBoard";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import PopMessage from "../common/messages/PopMessage";
import stateConfig from "../../config/state";

function LiveBoard({ watchlistWebSocket, liveParticipants, webSocketAPI }) {
  const [init, setInit] = useState(false);
  const [webinarIdInput, setWebinarIdInput] = useState("");
  const [error, setError] = useState(null);

  const POLLING_INTERVAL = 5000;  // 5 seconds

  const { isConnected, isReconnecting, webinarId, responseStatus, responseErr } = watchlistWebSocket;
  const { participants } = liveParticipants;

  useEffect(() => {
    if (!init) {
      webSocketAPI.getWatchlistWebSocketStatus();
      setInit(true);
    }
  }, [init, webSocketAPI]);

  useEffect(() => {
    if (!isConnected) return;

    webSocketAPI.getLiveParticipants();

    const timer = setInterval(() => {
      webSocketAPI.getLiveParticipants();
      webSocketAPI.getWatchlistWebSocketStatus(); 
    }, POLLING_INTERVAL);

    return () => clearInterval(timer);
  }, [isConnected, webSocketAPI]);

  useEffect(() => {
    if (responseStatus === stateConfig.responseStatus.ERROR) {
      setError(responseErr);
    }
  }, [responseStatus, responseErr]);

  useEffect(() => {
    if (liveParticipants.responseStatus === stateConfig.responseStatus.ERROR) {
      setError(liveParticipants.responseErr);
    }
  }, [liveParticipants.responseStatus, liveParticipants.responseErr]);

  const clearPopMessage = () => setError(null);

  const handleAddressed = (memberId, checked) => {
    webSocketAPI.updateParticipantAddressed(memberId, checked);
  }

  const handleSetWebinarId = () => {
    const normalizedId = webinarIdInput.replaceAll(" ", "");
    webSocketAPI.setWebinarId(normalizedId);
    setWebinarIdInput("");
  }

  const resetAddressed = () =>  webSocketAPI.resetAddressedParticipants();

  const getTypeClass = (type) => {
    if (type === "board-member" || type === "ecclesiastical-board-member") return "type-board";
    if (type === "large-donor") return "type-donor";
    return "";
  }

  return (
    <div className="WatchlistManager LiveBoard">
      <div className="heading-container">
        <h1>Zoomboard Live</h1>
        <div className="webinar-input">
          <input 
            type="text"
            placeholder="Webinar ID"
            value={webinarIdInput}
            disabled={isConnected}
            onChange={(e) => setWebinarIdInput(e.target.value)}
          />
        <Button color="primary" variant="contained" disabled={isConnected} onClick={handleSetWebinarId}>Set Webinar ID</Button>
      </div>
    </div>

    {error !== null &&
      <PopMessage horizontal="center" open={true} onClose={clearPopMessage}
        type={stateConfig.messageTypes.ERROR}>
          <p>
            There was an error with your request. Please try again.<br/>
            {error?.code && error?.message ? `ERROR: ${error.code} - ${error.message}` : ""}
          </p>
      </PopMessage>
    }

    {webinarId && (
      <div className="websocket-buttons">
        <div className="connection">
          <Button 
            color="primary" 
            variant="contained" 
            disabled={isConnected || isConnected === null}
            onClick={() => webSocketAPI.connectWatchlistWebSocket(webinarId)}>
              Start Connection
          </Button>
          <Button
            color="secondary" 
            variant="contained"
            onClick={() => webSocketAPI.disconnectWatchlistWebSocket(webinarId)}>
              Stop Connection
          </Button>
        </div>
        <div className="connection">
          <Button 
            color="primary" 
            variant="outlined"
            disabled={!isConnected}
            onClick={() => webSocketAPI.syncZoomParticipants(webinarId)}>
              Sync With Zoom
          </Button>
          <Button
            color="secondary"
            variant="outlined"
            onClick={resetAddressed}>
              Reset Addressed
          </Button>
        </div>
      </div>
    )}

    <div className="websocket-status">
      <p>WebSocket Status: <span className={isReconnecting ? "reconnecting" : isConnected ? "connected" : "disconnected"}>  
        {isReconnecting ? "RECONNECTING" : isConnected ? "CONNECTED" : "DISCONNECTED"}
        </span>
      </p>
      <p>Webinar ID: {webinarId ? webinarId : "NONE"}</p>
    </div>

    <div className="watchlist-container">
      <div className="watchlist-heading-row watchlist-row">
        <span>NAME</span>
        <span>EMAIL</span>
        <span>TYPE</span>
        <span>NOTES</span>
        <span>ADDRESSED</span>
      </div>

      <div>
        {participants.length === 0 && (
          <p className="watchlist-empty">No participants in this webinar.</p>
        )}
        {participants.map((participant) => (
          <div key={participant._id} className={`watchlist-row ${getTypeClass(participant.type)}`}>
            <span className="cell">{participant.name}</span>
            <span className="cell">{participant.email}</span>
            <span className="cell">{participant.type}</span>
            <span className="cell">{participant.notes}</span>
            <div className="member-actions">
              <Checkbox
                size="small"
                checked={participant.addressed}
                onChange={(event) => handleAddressed(participant._id, event.target.checked)}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
  )
}

function mapStateToProps(state) {
  return {
    watchlistWebSocket: state.watchlistWebSocket,
    liveParticipants: state.liveParticipants
  };
}

function mapDispatchToProps(dispatch) {
  return {
    webSocketAPI: bindActionCreators(webSocketAPI, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(LiveBoard);